import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Save, Loader2, RotateCcw, ChevronDown, ChevronRight, FileText, Languages,
} from "lucide-react";

const GROUPS = ["landing", "features", "pricing", "footer", "common"];

export default function ContentTab() {
  const [rows, setRows] = useState([]);
  const [openGroup, setOpenGroup] = useState("landing");
  const [drafts, setDrafts] = useState({}); // {slug: {es-AR: "...", en: "..."}}
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/content");
      setRows(data.items || []);
    } catch {
      toast.error("Failed to load content");
    }
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const out = {};
    for (const r of rows) {
      (out[r.group] = out[r.group] || []).push(r);
    }
    return out;
  }, [rows]);

  const filtered = (group) => {
    const list = grouped[group] || [];
    if (!filter) return list;
    const f = filter.toLowerCase();
    return list.filter(r => r.slug.toLowerCase().includes(f) || JSON.stringify(r.values || {}).toLowerCase().includes(f));
  };

  const save = async (slug) => {
    const d = drafts[slug];
    if (!d) return;
    setBusy(slug);
    try {
      await api.patch(`/admin/content/${encodeURIComponent(slug)}`, { values: d });
      toast.success("Saved");
      setDrafts(({ [slug]: _, ...rest }) => rest);
      await load();
      window.dispatchEvent(new Event("content:refresh"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(null); }
  };

  const reset = async (slug) => {
    if (!confirm("Reset this block to its seeded default?")) return;
    setBusy(slug);
    try {
      await api.post(`/admin/content/reset/${encodeURIComponent(slug)}`);
      toast.success("Reset to default");
      setDrafts(({ [slug]: _, ...rest }) => rest);
      await load();
      window.dispatchEvent(new Event("content:refresh"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reset failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-6" data-testid="content-tab">
      <div className="border border-border bg-card p-6">
        <h3 className="font-display font-bold tracking-tight text-lg">Contenido del sitio · Site content</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Editá toda la copia del sitio público (landing, features, pricing) en español argentino e inglés. Los cambios se reflejan al instante.
        </p>
        <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrar por slug o contenido…" className="mt-4 rounded-sm max-w-md" data-testid="content-filter" />
      </div>

      <div className="space-y-3">
        {GROUPS.map((g) => {
          const items = filtered(g);
          if (filter && items.length === 0) return null;
          const isOpen = openGroup === g;
          return (
            <div key={g} className="border border-border rounded-sm bg-card overflow-hidden" data-testid={`group-${g}`}>
              <button onClick={() => setOpenGroup(isOpen ? null : g)} className="w-full px-5 py-4 flex items-center justify-between hover:bg-muted/30 transition">
                <div className="flex items-center gap-2.5">
                  {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="font-display font-bold tracking-tight capitalize">{g}</span>
                  <span className="text-xs font-mono text-muted-foreground">{(grouped[g] || []).length} blocks</span>
                </div>
              </button>
              {isOpen && (
                <div className="border-t border-border divide-y divide-border">
                  {items.map((row) => (
                    <BlockEditor
                      key={row.slug}
                      row={row}
                      draft={drafts[row.slug]}
                      onChange={(lng, val) => setDrafts(d => ({ ...d, [row.slug]: { ...(d[row.slug] || {}), [lng]: val } }))}
                      onSave={() => save(row.slug)}
                      onReset={() => reset(row.slug)}
                      busy={busy === row.slug}
                    />
                  ))}
                  {items.length === 0 && <div className="px-5 py-6 text-sm text-muted-foreground font-mono">No blocks match the filter.</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BlockEditor({ row, draft, onChange, onSave, onReset, busy }) {
  const v = (lng) => (draft?.[lng] !== undefined ? draft[lng] : (row.values?.[lng] || ""));
  const dirty = !!draft && Object.keys(draft).length > 0;

  const isLong = (row.values?.["es-AR"] || "").length > 80 || (row.values?.["en"] || "").length > 80;

  return (
    <div className="px-5 py-4 space-y-3" data-testid={`block-${row.slug}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <code className="text-[11px] font-mono text-primary font-semibold">{row.slug}</code>
        <div className="flex gap-1.5">
          <Button size="sm" variant="ghost" onClick={onReset} disabled={busy} className="h-7 rounded-sm text-xs" data-testid={`reset-${row.slug}`}>
            <RotateCcw className="h-3 w-3 mr-1" /> Reset
          </Button>
          <Button size="sm" onClick={onSave} disabled={!dirty || busy} className="h-7 rounded-sm text-xs" data-testid={`save-${row.slug}`}>
            {busy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />} Save
          </Button>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-3">
        <LocaleField lng="es-AR" label="Español (AR)" value={v("es-AR")} onChange={(val) => onChange("es-AR", val)} isLong={isLong} testId={`field-${row.slug}-es`} />
        <LocaleField lng="en" label="English" value={v("en")} onChange={(val) => onChange("en", val)} isLong={isLong} testId={`field-${row.slug}-en`} />
      </div>
    </div>
  );
}

function LocaleField({ lng, label, value, onChange, isLong, testId }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1.5 flex items-center gap-1">
        <Languages className="h-3 w-3" /> {label}
      </div>
      {isLong ? (
        <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} className="rounded-sm text-sm" data-testid={testId} />
      ) : (
        <Input value={value} onChange={(e) => onChange(e.target.value)} className="rounded-sm text-sm" data-testid={testId} />
      )}
    </div>
  );
}
